import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Handshake, ShoppingBag, Wrench, ChevronDown, Flag, ArrowRight, MapPin, Camera } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { KineticHeadline } from "@/components/KineticHeadline";
import { GameMenuCard } from "@/components/GameMenuCard";
import { SectionHeading } from "@/components/SectionHeading";
import { Marquee } from "@/components/Marquee";
import { Badge } from "@/components/ui/badge";
import { PHOTOS, HERO_VIDEO } from "@/lib/assets";
import { getRaces, getProducts, resolveImageUrl } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function Home() {
  const [races, setRaces] = useState([]);
  const [products, setProducts] = useState([]);
  const { t, lp, lang, dateLocale } = useLang();
  const czk = (n) => (lang === "en" ? `${n.toLocaleString("en-GB")} CZK` : `${n.toLocaleString("cs-CZ")} Kč`);
  const smokeRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: smokeRef, offset: ["start end", "end start"] });
  const smokeY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  useEffect(() => {
    getRaces(2025)
      .then((data) => setRaces(data.filter((r) => r.status === "upcoming").slice(0, 3)))
      .catch(() => {});
    getProducts()
      .then((data) => setProducts(data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <PageWrap testId="home-page">
      {/* ============ HERO ============ */}
      <section data-testid="home-hero" className="relative flex h-screen min-h-[640px] items-center justify-center overflow-hidden">
        <video
          data-testid="home-hero-video"
          className="absolute inset-0 h-full w-full object-cover"
          src={HERO_VIDEO}
          poster={PHOTOS.motionBlur}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
        {/* overlays */}
        <div className="absolute inset-0 bg-[#07080A]/70" />
        <div className="hero-glow absolute inset-0" />
        <div className="scanlines absolute inset-0 opacity-70" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#07080A] to-transparent" />

        {/* HUD corners */}
        <div className="pointer-events-none absolute left-5 top-24 hidden h-10 w-10 border-l-2 border-t-2 border-[#1EA7FF]/60 md:block" />
        <div className="pointer-events-none absolute right-5 top-24 hidden h-10 w-10 border-r-2 border-t-2 border-[#1EA7FF]/60 md:block" />
        <div className="pointer-events-none absolute bottom-8 left-5 hidden h-10 w-10 border-b-2 border-l-2 border-[#B7FF2A]/50 md:block" />
        <div className="pointer-events-none absolute bottom-8 right-5 hidden h-10 w-10 border-b-2 border-r-2 border-[#B7FF2A]/50 md:block" />

        <div className="relative z-10 mx-auto max-w-6xl px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="mb-4 flex items-center justify-center gap-3 font-mono text-xs uppercase tracking-[0.35em] text-[#B7FF2A]"
          >
            <span className="inline-block h-[2px] w-8 bg-[#B7FF2A]/60" />
            {t("home.heroTag")}
            <span className="inline-block h-[2px] w-8 bg-[#B7FF2A]/60" />
          </motion.div>

          <KineticHeadline
            testId="hero-headline"
            text="FILIP VIMPEL"
            className="font-display text-[16vw] leading-[0.88] uppercase tracking-[0.02em] text-white sm:text-8xl lg:text-[10rem]"
          />

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mx-auto mt-5 max-w-2xl font-mono text-xs uppercase tracking-[0.22em] text-white/70 sm:text-sm"
          >
            {t("home.heroSubtitle")[0]}&nbsp;<span className="text-[#1EA7FF]">|</span>&nbsp;{t("home.heroSubtitle")[1]}&nbsp;<span className="text-[#1EA7FF]">|</span>&nbsp;{t("home.heroSubtitle")[2]}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="absolute -bottom-20 left-1/2 -translate-x-1/2"
          >
            <ChevronDown className="h-6 w-6 animate-bounce text-white/40" />
          </motion.div>
        </div>
      </section>

      {/* ============ CTA GAME CARDS ============ */}
      <section className="relative z-10 mx-auto -mt-24 max-w-6xl px-4 sm:px-6 lg:px-8">
        <div data-testid="home-hero-cta-grid" className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          <GameMenuCard
            to={lp("/spoluprace")}
            icon={Handshake}
            title={t("home.cta.partnershipTitle")}
            subtitle={t("home.cta.partnershipSub")}
            accent="blue"
            testId="home-cta-card-spoluprace"
            delay={0}
          />
          <GameMenuCard
            to={lp("/shop")}
            icon={ShoppingBag}
            title={t("home.cta.merchTitle")}
            subtitle={t("home.cta.merchSub")}
            accent="acid"
            testId="home-cta-card-merch"
            delay={0.12}
          />
          <GameMenuCard
            to={lp("/dilna")}
            icon={Wrench}
            title={t("home.cta.workshopTitle")}
            subtitle={t("home.cta.workshopSub")}
            accent="blue"
            testId="home-cta-card-motorsport-dilna"
            delay={0.24}
          />
        </div>
      </section>

      {/* marquee */}
      <div className="mt-20">
        <Marquee items={t("marquee.b")} />
      </div>

      {/* ============ ABOUT TEASER ============ */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="clip-corner relative overflow-hidden"
          >
            <img src={PHOTOS.redbullArch} alt="Filip Vimpel" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07080A]/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono text-xs uppercase tracking-[0.25em] text-white/80">
              // {t("home.raceStreets")}
            </div>
          </motion.div>

          <div>
            <SectionHeading kicker={t("home.aboutKicker")} title={t("home.aboutTitle")} testId="home-about-heading" />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6 leading-relaxed text-white/60"
            >
              {t("home.bio")}
            </motion.p>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {t("home.stats").map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.1 * i }}
                  className="clip-corner-sm border border-white/10 bg-[#0F1217] p-4"
                >
                  <div className="break-words font-mono text-xl font-semibold leading-tight text-[#1EA7FF] sm:text-2xl">{s.value}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-white/45">{s.label}</div>
                </motion.div>
              ))}
            </div>
            <Link
              to={lp("/o-mne")}
              data-testid="home-about-link"
              className="group mt-8 inline-flex items-center gap-2 font-display text-lg uppercase tracking-[0.14em] text-[#B7FF2A]"
            >
              {t("home.fullStory")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ SMOKE PARALLAX DIVIDER ============ */}
      <section ref={smokeRef} className="relative h-[46vh] min-h-[320px] overflow-hidden">
        <motion.img
          src={PHOTOS.smokeBarrier}
          alt="Drift smoke show"
          style={{ y: smokeY }}
          className="absolute inset-0 h-[130%] w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-[#07080A]/55" />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mx-auto max-w-4xl px-4 text-center"
          >
            <div className="font-display text-3xl uppercase leading-[1.1] tracking-[0.05em] text-white sm:text-5xl">
              {t("home.quote")}
              <span className="text-[#B7FF2A]">{t("home.quoteAccent")}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ RACES TEASER ============ */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <SectionHeading kicker={t("home.racesKicker")} title={t("home.racesTitle")} accent="acid" testId="home-races-heading" />
          <Link
            to={lp("/zavody")}
            data-testid="home-races-link"
            className="group inline-flex items-center gap-2 font-display text-lg uppercase tracking-[0.14em] text-[#1EA7FF]"
          >
            {t("home.racesLink")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </div>
        <div data-testid="home-races-grid" className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {races.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className="light-sweep clip-corner group border border-white/10 bg-[#0F1217] p-6 transition-colors duration-200 hover:border-[#B7FF2A]/40"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-[#B7FF2A]">
                  {new Date(r.date).toLocaleDateString(dateLocale, { day: "numeric", month: "long" })}
                </span>
                <Flag className="h-4 w-4 text-white/30" />
              </div>
              <div className="mt-3 font-display text-2xl uppercase tracking-[0.05em] leading-tight">{r.name}</div>
              <div className="mt-2 flex items-center gap-1.5 text-sm text-white/50">
                <MapPin className="h-3.5 w-3.5" /> {r.location} ({r.country})
              </div>
              <Badge className="mt-4 rounded-sm border border-[#1EA7FF]/40 bg-[#1EA7FF]/10 font-mono text-[10px] uppercase tracking-[0.16em] text-[#1EA7FF] hover:bg-[#1EA7FF]/10">
                {r.series}
              </Badge>
            </motion.div>
          ))}
        </div>
      </section>

      <Marquee items={t("marquee.a")} accent />

      {/* ============ MERCH TEASER ============ */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <SectionHeading kicker={t("home.merchKicker")} title={t("home.merchTitle")} testId="home-merch-heading" />
          <Link
            to={lp("/shop")}
            data-testid="home-merch-link"
            className="group inline-flex items-center gap-2 font-display text-lg uppercase tracking-[0.14em] text-[#B7FF2A]"
          >
            {t("home.merchLink")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </div>
        <div data-testid="home-merch-grid" className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {products.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
            >
              <Link to={lp("/shop")} className="group block">
                <div className="clip-corner relative overflow-hidden border border-white/10">
                  <img
                    src={resolveImageUrl(p.image)}
                    alt={p.name}
                    className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {p.badge && (
                    <span className="absolute left-3 top-3 bg-[#B7FF2A] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#07080A]">
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-display text-lg uppercase tracking-[0.06em] text-white/85">{p.name}</span>
                  <span className="font-mono text-sm text-[#B7FF2A]">{czk(p.price)}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ MEDIA STRIP ============ */}
      <section className="border-t border-white/10 bg-[#0B0D10] py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <SectionHeading kicker={t("home.mediaKicker")} title={t("home.mediaTitle")} accent="acid" testId="home-media-heading" />
            <Link
              to={lp("/media")}
              data-testid="home-media-link"
              className="group inline-flex items-center gap-2 font-display text-lg uppercase tracking-[0.14em] text-[#1EA7FF]"
            >
              <Camera className="h-4 w-4" /> {t("home.mediaLink")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[PHOTOS.motionBlur, PHOTOS.wheelPaddock, PHOTOS.smokeBarrier, PHOTOS.paddockTent].map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.08 * i }}
                className="clip-corner-sm group relative overflow-hidden border border-white/10"
              >
                <img
                  src={src}
                  alt={t("home.galleryAlt")}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#1EA7FF]/0 transition-colors duration-300 group-hover:bg-[#1EA7FF]/10" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </PageWrap>
  );
}
